'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function GozAltiDolgusununTehlikeleri() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/goz-alti-dolgusunun-tehlikeleri-bilimsel-gercekler-ve-guvenli-uygulama-kriterleri';
  const pageTitle = 'Göz Altı Dolgusunun Tehlikeleri: Bilimsel Gerçekler ve Güvenli Uygulama Kriterleri';
  const pageDescription = 'Göz altı dolgusu riskli mi? Tyndall etkisi, damar tıkanması, morarma ve enfeksiyon gibi tehlikeler — bilimsel ve klinik açıklamalarla ele alındı.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Göz Altı Dolgusunun Tehlikeleri', href: '/tr/blog/goz-alti-dolgusunun-tehlikeleri-bilimsel-gercekler-ve-guvenli-uygulama-kriterleri' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Göz altı dolgusu tehlikeleri ve güvenli uygulama"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Göz Altı Dolgusunun Tehlikeleri: Bilimsel Gerçekler ve Güvenli Uygulama Kriterleri
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>14 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>18 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-red-400 text-white rounded-full hover:bg-red-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-red-700 text-white rounded-full hover:bg-red-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Göz altı dolgusu, yorgun ve çökmüş görünümü düzeltmek için yapılan en popüler işlemlerden biridir.
                Ancak bu bölge yüzün en ince, en hassas ve en damar açısından zengin bölgesidir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu yüzden, yanlış teknikle yapılan uygulamalarda ciddi komplikasyonlar ortaya çıkabilir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium text-lg">
                  "Göz altı dolgusu basit bir işlem değildir; milimetrelerle oynarsınız, ama sonucu yıllarca taşırsınız."
                </p>
                <p className="text-red-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Göz Altı Anatomisi: Neden Bu Kadar Hassas?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu bölgedeki deri, ortalama 0.5 mm kalınlığındadır — yani vücudun en ince deri tabakası.
                Altında ise yoğun damar ağı (infraorbital damarlar) ve sinirler bulunur.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolayısıyla burada yapılacak her enjeksiyon, doğru derinlik ve yön gerektirir.
                Aksi halde hem kozmetik hem de tıbbi komplikasyonlar görülebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Göz Altı Dolgusunun En Yaygın Tehlikeleri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Görülme Sıklığı</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Tyndall Etkisi</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgunun yüzeye yakın kalmasıyla mavi-gri renk görünümü</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">%10'a kadar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Damar Tıkanması (Vasküler Oklüzyon)</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgunun damar içine girmesiyle kan akışının durması</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">%0.01 ama ciddi risk</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Asimetri</td>
                      <td className="border border-gray-300 px-4 py-3">Eşit olmayan enjeksiyon miktarı</td>
                      <td className="border border-gray-300 px-4 py-3 text-orange-600 font-medium">%5–8</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Enfeksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Sterilite eksikliği</td>
                      <td className="border border-gray-300 px-4 py-3 text-orange-600 font-medium">%1–2</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ödem (şişlik)</td>
                      <td className="border border-gray-300 px-4 py-3">Fazla miktarda dolgu veya hassas doku tepkisi</td>
                      <td className="border border-gray-300 px-4 py-3 text-orange-600 font-medium">%15</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Bu oranlar yalnızca hekim dışı ortamlarda çok daha yüksektir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Tyndall Etkisi Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz altı derisi çok ince olduğundan, dolgu maddesi fazla yüzeyde kalırsa ışığı yansıtır.
                Bu da mavi veya mor renkte bir gölge oluşturur.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-blue-600 mr-2">🔹</span>
                    <span className="text-blue-800"><strong>Sebep:</strong> Hyaluronik asidin ışığı kırma özelliği</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-600 mr-2">🔹</span>
                    <span className="text-blue-800"><strong>Çözüm:</strong> Hyaluronidaz enzimi ile dolgunun eritilmesi</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-blue-600 mr-2">🔹</span>
                    <span className="text-blue-800"><strong>Önleme:</strong> Derin plan (periosteal düzlem) enjeksiyonu ve uygun yoğunlukta ürün seçimi</span>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Damar Tıkanması: En Ciddi Risk
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Damar içine yanlışlıkla dolgu verilirse kan akışı durur ve o bölgeye oksijen gitmez.
                Bu durum ciltte solukluk → morarma → nekroz (doku ölümü) şeklinde ilerler.
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-red-900 mb-4">Belirtiler:</h4>
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Ani ağrı</li>
                  <li>Solgunluk</li>
                  <li>Morarma</li>
                  <li>Görme bulanıklığı (nadir ama ciddi)</li>
                </ul>
                <p className="text-red-800 mt-4">
                  <strong>Acil durumda</strong> hemen hyaluronidaz enzimi uygulanmalı ve sıcak kompres yapılmalıdır.
                  Zamanında müdahale edilmezse kalıcı iz veya görme kaybı gelişebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Alerjik Reaksiyonlar
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Kullanılan dolgu maddesi genellikle vücutla uyumlu hyaluronik asittir, ancak nadiren lokal alerjik tepkiler olabilir.
                Bu durumda:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Kızarıklık</li>
                  <li>Kaşıntı</li>
                  <li>Hafif ödem</li>
                </ul>
                <p className="text-yellow-800 mt-4">
                  görülür ve genellikle kısa sürede geçer.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Şişlik (Ödem) Neden Olur?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz altı bölgesi su tutmaya çok yatkındır.
                Hyaluronik asit de su bağlayıcı özellikte olduğu için fazla uygulama durumunda kalıcı ödem oluşabilir.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Neden</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Çözüm</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Fazla dolgu miktarı</td>
                      <td className="border border-gray-300 px-4 py-3">Hyaluronidaz ile çözme</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yanlış dolgu tipi</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük viskoziteli ürün tercih edilmeli</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Lenfatik drenaj bozukluğu</td>
                      <td className="border border-gray-300 px-4 py-3">Masaj ve soğuk uygulama</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Enfeksiyon Riski
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz çevresi steril olmayan koşullarda enjeksiyona çok hassastır.
                Bakteri bulaşırsa enfeksiyon gelişebilir ve ciltte apseler oluşabilir.
                Bu durumda:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Antibiyotik</li>
                  <li>Dolgunun eritilmesi</li>
                  <li>Drenaj gerekebilir.</li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  "Steril olmayan bir ortamda yapılan göz altı dolgusu, estetik değil, enfeksiyon riski taşır."
                </p>
                <p className="text-red-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Uygulama Kim Tarafından Yapılmalı?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Uygulayıcı</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Güvenli mi?</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Plastik cerrah</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Damar planı bilgisi üst düzey</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dermatolog</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt derinliği ve doku reaksiyonu konusunda uzman</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Medikal estetik hekimi</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Özel eğitimli tıp doktoru</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kozmetolog / estetisyen</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Tıbbi bilgi ve enjeksiyon yetkisi yok</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Göz Altı Dolgusu Sonrası Görülebilecek Hafif Etkiler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Normal mi?</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif kızarıklık</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">24 saat</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Morarma</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">3–5 gün</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 gün</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Mavi yansıma</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">⚠️ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Tyndall etkisi belirtisi</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ağrı / yanma</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">⚠️ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Damar tıkanması olabilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Göz altı dolgusu kalıcı hasar bırakır mı?</h3>
                  <p className="text-gray-600">Yanlış uygulamalarda evet; hekim tarafından yapılırsa hayır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Morarma normal mi?</h3>
                  <p className="text-gray-600">Evet, 3–5 gün içinde geçer.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Göz altı morluğunu tamamen giderir mi?</h3>
                  <p className="text-gray-600">Hayır, damar rengi koyu olan kişilerde sadece kısmen düzelme olur.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Tyndall etkisi olursa ne yapılır?</h3>
                  <p className="text-gray-600">Hyaluronidaz enzimiyle dolgu eritilmelidir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Göz altı dolgusu ne kadar sürer?</h3>
                  <p className="text-gray-600">Uzman hekim tarafından yapıldığında 6-12 ay arası etkili olur.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Hangi dolgu markaları güvenli?</h3>
                  <p className="text-gray-600">Juvederm, Restylane, Teosyal gibi Sağlık Bakanlığı onaylı markalar güvenlidir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Göz altı dolgusu, estetik tıbbın en hassas uygulamasıdır.
                  Bir milimetre fazla derinlik güzelliği artırır, bir milimetre sığlık morluk yaratır."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Etik ve Klinik Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu bölgeye yapılacak her işlem, doktorun bilgi ve deneyimine emanet edilmelidir.
                Göz altı dolgusu; yanlış ellerde risk, doğru ellerde sanat demektir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 "Estetikte en riskli yer, en dikkatli olunması gereken yerdir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣3️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz altı dolgusu doğru hekim ve doğru teknikle yapıldığında güvenlidir.
                Ancak damar tıkanması, Tyndall etkisi ve ödem gibi komplikasyonlardan korunmak için yalnızca uzman hekim tercih edilmelidir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Göz altı dolgusu yaptırmadan önce aynaya değil, hekimin eğitimine bak."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Tear Trough Filler Complication Review"</li>
                  <li>FDA (2023) – "Vascular Occlusion and Filler Safety"</li>
                  <li>Harvard Medical Aesthetics (2024) – "Tyndall Effect in Hyaluronic Acid Fillers"</li>
                  <li>TETD (2024) – "Göz Altı Dolgusu Güvenlik Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#gözaltıdolgu</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#estetikgüvenlik</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#tyndallefekti</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#dolguriski</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-red-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Güvenli Göz Altı Dolgusu İçin Randevu Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan güvenli göz altı dolgusu uygulaması hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Güvenli göz altı dolgusu hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-red-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/goz-alti-isik-dolgusuna-uygunluk-kimler-icin-ideal-kimler-icin-degil"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Göz Altı Işık Dolgusuna Uygunluk</h4>
              <p className="text-gray-600 text-sm">Kimler için ideal, kimler için değil? Göz altı dolgusu uygunluk kriterleri.</p>
            </a>
            <a
              href="/tr/blog/isik-dolgusunun-goz-alti-morluklarina-etkisi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Işık Dolgusunun Göz Altı Morluklarına Etkisi</h4>
              <p className="text-gray-600 text-sm">Göz altı morluklarına etkisi ve sonuçları hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Doğal, simetrik ve estetik sonuçlar için dolgu teknikleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
